/**
 * 진단 위상 시리즈 데이터 소스. 에피소드 위상 그리드(envelope)와 컬렉션 기준 밴드를 다룬다.
 *
 * Owns:
 * - 시리즈 아티팩트 JSON(signed URL 본문)을 위상 그리드 모델로 파싱하고 검증한다.
 * - 진단 상세/요약 GET 다음 아티팩트 GET 을 하는 2단계 로딩(순차 2 RTT). 에피소드 쪽은 두 단계를
 *   따로 export 한다. 그래서 소비처는 첫 응답으로 그릴 수 있는 것을 먼저 그린다.
 *
 * Does not own:
 * - 진단 API 경계(`src/api/diagnostics` 가 소유).
 * - HTTP client 정책(`src/http/signedResource` 가 소유).
 * - 이탈률 계산·스파크라인 기하(`bandDeviation`·`bandSparkline` 이 소유).
 *
 * 백엔드 계약(backend `worker/derived/_diagnostic.py`·`service/diagnostics/aggregate.py`):
 * - 아티팩트 가로축은 시간이 아니라 **행 위상**(`axis_kind:"row_phase"`, 길이 `resample_points`)이다.
 * - 유효 값이 없는 버킷은 0 이 아니라 **null** 이다. 파서는 이 값을 NaN 으로 바꾼다. 그래서
 *   렌더는 선을 끊고, 이 점은 이탈률 분모에서 빠진다.
 * - 에피소드 그리드와 밴드는 `resample_points` 가 같아야 겹칠 수 있다.
 */

import {
  EPISODE_PHASE_ENVELOPE_KEY,
  EPISODE_TIMELINE_KEY,
  REFERENCE_PHASE_BAND_KEY,
  diagnosticSeriesOf,
  type DiagnosticsSeriesResp,
  getCollectionDiagnostics,
  getEpisodeDiagnostics,
  type ChannelIndicatorResp,
  type DiagnosticsFailureResp,
  type DiagnosticsStatus,
  type EpisodeDiagnosticsStatus,
} from "@/api";
import { qualityCandidatesOf } from "@/api/diagnosticQualityCandidates";
import { episodeSegmentsOf } from "@/api/diagnosticSegments";
import type { ChannelSegmentsResp, QualityCandidatesResp } from "@/api/diagnostics";
import { fetchSignedJson } from "@/http";
import { isAbortError } from "@/utils";

/**
 * 이 모듈이 읽는 아티팩트 본문의 스키마 버전. 버전이 다르면 형식 불명으로 보고 degrade 한다.
 *
 * 한 버전만 받는다. 버전이 다르다는 것은 가로축과 통계의 뜻이 다르다는 선언이다. 형식이 우연히
 * 맞아도 그 본문을 읽으면 다른 뜻의 값을 같은 눈금에 겹치게 된다.
 */
export const PHASE_ARTIFACT_SCHEMA_VERSION = "5";

const EPISODE_GRID_VALUE_STAT = "bucket_extrema_chordal_pose_mean";
const REFERENCE_BAND_VALUE_STAT = "position_quantiles_rotation_radius";

/** 한 에피소드의 위상 그리드. 채널별, dim 별 `[T]` min/max/mean 이다. */
export type EpisodePhaseGrid = {
  resamplePoints: number;
  channels: Map<string, EpisodeChannelGrid>;
};

export type EpisodeChannelGrid = {
  min: number[][];
  max: number[][];
  mean: number[][];
};

/** 컬렉션 기준 밴드. 채널별, dim 별 `[T]` p10/median/p90 과 support(에피소드 수)다. */
export type ReferenceBandGrid = {
  resamplePoints: number;
  channels: Map<string, ReferenceChannelBand>;
};

export type ReferenceChannelBand = {
  episodes: number;
  low: number[][];
  median: number[][];
  high: number[][];
  // 7D pose 는 자세 네 성분 대신 대표 회전까지의 각도를 비교한다.
  rotation?: ReferenceRotationBand;
};

export type ReferenceRotationBand = {
  referenceWxyz: number[][];
  median: number[];
  high: number[];
};

/**
 * 에피소드 위상 그리드 아티팩트(`value_stat:"bucket_extrema_chordal_pose_mean"`)를 파싱한다.
 * schema_version 이나 value_stat 이 맞지 않거나 형식이 불명이면 null 이다.
 */
export function parseEpisodePhaseGrid(raw: unknown): EpisodePhaseGrid | null {
  const payload = artifactPayload(raw, EPISODE_GRID_VALUE_STAT);
  if (payload === null) {
    return null;
  }
  const channels = new Map<string, EpisodeChannelGrid>();
  for (const [name, value] of Object.entries(payload.channels)) {
    const channel = parseEpisodeChannel(value);
    if (channel !== null) {
      channels.set(name, channel);
    }
  }
  return { resamplePoints: payload.resamplePoints, channels };
}

/**
 * 기준 밴드 아티팩트(`value_stat:"position_quantiles_rotation_radius"`)를 파싱한다. 밴드를
 * 만들지 못한 채널은 본문의 `skipped_channels` 로 온다. "channels 에 없음"으로 충분하므로
 * 이 목록은 따로 담지 않는다.
 *
 * 지점마다 기여 에피소드가 최소 support 미만이면 분위수가 null 로 온다. 모든 지점이 이렇게
 * 비어 있는 일반 채널은 거른다. 회전 채널은 대표 자세가 없는 경우도 비교 불가로 표시한다.
 */
export function parseReferenceBandGrid(raw: unknown): ReferenceBandGrid | null {
  const payload = artifactPayload(raw, REFERENCE_BAND_VALUE_STAT);
  if (payload === null) {
    return null;
  }
  const channels = new Map<string, ReferenceChannelBand>();
  for (const [name, value] of Object.entries(payload.channels)) {
    const channel = parseBandChannel(value, payload.resamplePoints);
    if (channel !== null) {
      channels.set(name, channel);
    }
  }
  return { resamplePoints: payload.resamplePoints, channels };
}

/** 에피소드 위상 그리드 로딩 결과. 그리드, 상태 문구, 데이터 없음 중 하나다. */
export type EpisodePhaseGridResult =
  | { kind: "grid"; grid: EpisodePhaseGrid }
  | {
      kind: "status";
      status: DiagnosticsStatus;
      // 실패 사유와 산출(시도) 시각. failed placeholder 문구에 병기한다.
      failure: DiagnosticsFailureResp | null;
      computedAt: string | null;
    }
  | { kind: "unavailable" };

/**
 * 에피소드 진단 상세의 첫 응답. 그리드를 받기 전에 그릴 수 있는 것을 모두 담는다.
 *
 * 채널 사실은 이 응답에 이미 포함된다. 그래서 그리드 아티팩트를 기다리지 않고 먼저 쓴다.
 * 그리드는 `gridSeries.url` 로 한 번 더 받는다(2번째 RTT).
 */
export type EpisodeDiagnosticDetail = {
  status: EpisodeDiagnosticsStatus;
  failure: DiagnosticsFailureResp | null;
  computedAt: string | null;
  // 단건 상세는 카메라·상태 채널을 포함한 모든 채널을 준다.
  channels: readonly ChannelIndicatorResp[];
  // 범주형 채널 값 구간. 빈 목록의 뜻은 `actionSourceSegmentsState` 가 채널 구조를 보고 구분한다.
  segments: readonly ChannelSegmentsResp[];
  // video 진단의 품질 후보. 구 BE 응답은 미분석으로 읽는다.
  qualityCandidates: QualityCandidatesResp;
  // 각 파일의 상태·처리 오류와 ready URL 을 함께 보존한다.
  gridSeries: DiagnosticsSeriesResp | null;
  timelineSeries: DiagnosticsSeriesResp | null;
  motionOverviewSeries: DiagnosticsSeriesResp | null;
  motionDetailSeries: DiagnosticsSeriesResp | null;
};

/**
 * 에피소드 진단 상세를 조회한다(1 RTT). 404(부재·가시성)는 null 이다.
 *
 * `declined` 는 오지 않는다. 에피소드 진단은 데이터셋의 밴드 집계 플래그와 관계없이 항상
 * 실행된다. 그 플래그는 컬렉션 상태(`CollectionBandState`)에만 포함된다.
 */
export async function loadEpisodeDiagnosticDetail(
  datasetUuid: string,
  episodeUuid: string,
  options: { signal?: AbortSignal } = {},
): Promise<EpisodeDiagnosticDetail | null> {
  const detail = await getEpisodeDiagnostics(datasetUuid, episodeUuid, {
    signal: options.signal,
  });
  if (detail === null) {
    return null;
  }
  return {
    status: detail.diagnostics_status,
    failure: detail.failure,
    computedAt: detail.computed_at,
    channels: detail.channels,
    segments: episodeSegmentsOf(detail),
    qualityCandidates: qualityCandidatesOf(detail),
    gridSeries: diagnosticSeriesOf(detail.series, EPISODE_PHASE_ENVELOPE_KEY),
    timelineSeries: diagnosticSeriesOf(detail.series, EPISODE_TIMELINE_KEY),
    motionOverviewSeries: diagnosticSeriesOf(detail.series, "episode_motion_overview"),
    motionDetailSeries: diagnosticSeriesOf(detail.series, "episode_motion_detail"),
  };
}

/**
 * 상세 응답이 가리키는 위상 그리드 아티팩트를 로드한다(2번째 RTT).
 *
 * 결과는 다음과 같다:
 * - 상세나 series 가 없으면 unavailable 이다.
 * - 개별 항목이 ready 가 아니면 status 다.
 * - ready 인데 URL 이 없거나 파싱에 실패하면 unavailable 이다.
 *
 * 아티팩트 fetch 네트워크 오류는 전파한다. 소비처가 에러 placeholder 를 렌더한다.
 */
export async function loadEpisodePhaseGridFor(
  detail: EpisodeDiagnosticDetail | null,
  options: { signal?: AbortSignal } = {},
): Promise<EpisodePhaseGridResult> {
  if (detail === null) {
    return { kind: "unavailable" };
  }
  const series = detail.gridSeries;
  if (series === null) return { kind: "unavailable" };
  if (series.status !== "ready") {
    return {
      kind: "status",
      status: series.status,
      failure: series.failure,
      computedAt: detail.computedAt,
    };
  }
  if (series.url === null) {
    return { kind: "unavailable" };
  }
  const grid = parseEpisodePhaseGrid(await fetchSignedJson<unknown>(series.url, options.signal));
  return grid === null ? { kind: "unavailable" } : { kind: "grid", grid };
}

/** 컬렉션 기준 밴드 상태. 상태 문구(status)와 밴드 본문(band)을 분리해 전달한다. */
export type CollectionBandState = {
  // null 은 컬렉션 조회 실패(404 포함)다. 그때는 상태 문구 없이 밴드만 숨긴다.
  status: DiagnosticsStatus | null;
  // ready 인 개별 밴드 아티팩트의 로드와 검증이 끝났을 때만 표시한다.
  band: ReferenceBandGrid | null;
  // 컬렉션 산출 시각. 캡션에 "언제 잰 값인가"를 병기한다. 미산출이거나 조회에 실패하면 null 이다.
  computedAt: string | null;
};

/**
 * 컬렉션 진단 요약을 조회하고 기준 밴드 아티팩트를 로드한다(컬렉션당 1회, 순차 2 RTT).
 * 밴드는 참고용 오버레이다. 그래서 아티팩트 fetch·파싱 실패는 전파하지 않고 상태만 남긴다.
 */
export async function loadCollectionBand(
  datasetUuid: string,
  options: { signal?: AbortSignal } = {},
): Promise<CollectionBandState> {
  const summary = await getCollectionDiagnostics(datasetUuid);
  if (summary === null) {
    return { status: null, band: null, computedAt: null };
  }
  const series = diagnosticSeriesOf(summary.series, REFERENCE_PHASE_BAND_KEY);
  if (!series?.url) {
    return { status: summary.status, band: null, computedAt: summary.computed_at };
  }
  try {
    const band = parseReferenceBandGrid(await fetchSignedJson<unknown>(series.url, options.signal));
    return { status: summary.status, band, computedAt: summary.computed_at };
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    return { status: summary.status, band: null, computedAt: summary.computed_at };
  }
}

// ── 파싱 내부 구현 ──

type ArtifactPayload = {
  resamplePoints: number;
  channels: Record<string, unknown>;
};

/** 아티팩트 공통 envelope(schema_version·value_stat·resample_points·channels)를 검증한다. */
function artifactPayload(raw: unknown, valueStat: string): ArtifactPayload | null {
  if (raw === null || typeof raw !== "object") {
    return null;
  }
  const payload = raw as {
    schema_version?: unknown;
    value_stat?: unknown;
    resample_points?: unknown;
    channels?: unknown;
  };
  if (
    payload.schema_version !== PHASE_ARTIFACT_SCHEMA_VERSION ||
    payload.value_stat !== valueStat
  ) {
    return null;
  }
  if (typeof payload.resample_points !== "number" || payload.resample_points <= 0) {
    return null;
  }
  if (payload.channels === null || typeof payload.channels !== "object") {
    return null;
  }
  return {
    resamplePoints: payload.resample_points,
    channels: payload.channels as Record<string, unknown>,
  };
}

function parseEpisodeChannel(value: unknown): EpisodeChannelGrid | null {
  if (value === null || typeof value !== "object") {
    return null;
  }
  const channel = value as { min?: unknown; max?: unknown; mean?: unknown };
  const min = toNumberMatrix(channel.min);
  const max = toNumberMatrix(channel.max);
  const mean = toNumberMatrix(channel.mean);
  if (min === null || max === null || mean === null || min.length === 0) {
    return null;
  }
  return { min, max, mean };
}

function parseBandChannel(value: unknown, points: number): ReferenceChannelBand | null {
  if (value === null || typeof value !== "object") {
    return null;
  }
  const channel = value as {
    episodes?: unknown;
    low?: unknown;
    median?: unknown;
    high?: unknown;
    rotation?: unknown;
  };
  const low = toNumberMatrix(channel.low);
  const median = toNumberMatrix(channel.median);
  const high = toNumberMatrix(channel.high);
  if (low === null || median === null || high === null || low.length === 0) {
    return null;
  }
  const rotation =
    channel.rotation === undefined ? null : parseRotationBand(channel.rotation, points);
  // 알 수 없는 회전 계약을 위치만 있는 밴드로 읽으면 이탈률 분모가 달라진다.
  if (channel.rotation !== undefined && (rotation === null || low.length !== 7)) {
    return null;
  }
  if (rotation === null && !hasComparablePoint(low, high)) {
    return null;
  }
  return {
    episodes: typeof channel.episodes === "number" ? channel.episodes : 0,
    low,
    median,
    high,
    ...(rotation === null ? {} : { rotation }),
  };
}

function parseRotationBand(value: unknown, points: number): ReferenceRotationBand | null {
  if (value === null || typeof value !== "object") return null;
  const rotation = value as {
    method?: unknown;
    unit?: unknown;
    outside?: unknown;
    reference_wxyz?: unknown;
    median?: unknown;
    high?: unknown;
  };
  if (
    rotation.method !== "chordal_mean_geodesic_radius" ||
    rotation.unit !== "rad" ||
    rotation.outside !== "angle_gt_p90"
  )
    return null;
  const referenceWxyz = toNumberMatrix(rotation.reference_wxyz);
  const radii = toNumberMatrix([rotation.median, rotation.high]);
  if (
    referenceWxyz === null ||
    referenceWxyz.length !== 4 ||
    referenceWxyz.some((row) => row.length !== points) ||
    radii === null ||
    radii.some((row) => row.length !== points)
  )
    return null;
  return { referenceWxyz, median: radii[0], high: radii[1] };
}

// low·high 가 모두 유한한 지점이 하나라도 있는지 확인한다. 이것이 이탈률을 비교할 수 있는 최소 조건이다.
function hasComparablePoint(low: number[][], high: number[][]): boolean {
  return low.some((row, dim) =>
    row.some((value, i) => Number.isFinite(value) && Number.isFinite(high[dim]?.[i])),
  );
}

/**
 * `[dim][T]` 숫자 행렬로 정규화한다. 배열이 아니면 null 이다. 유효 값이 없는 버킷의
 * null(백엔드 계약)은 0 이 아니라 NaN 으로 바꾼다. 그래서 렌더는 선을 끊고, 이 점은
 * 이탈률 분모에서 빠진다.
 */
function toNumberMatrix(value: unknown): number[][] | null {
  if (!Array.isArray(value)) {
    return null;
  }
  const matrix: number[][] = [];
  for (const row of value) {
    if (!Array.isArray(row)) {
      return null;
    }
    matrix.push(row.map((cell) => (typeof cell === "number" ? cell : Number.NaN)));
  }
  return matrix;
}
